import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Skeleton} from 'primeng/skeleton';
import {ButtonIconComponent} from '../../../../../shared/components/button-icon/button-icon.component';
import {DialogBase} from '../../../../../shared/components/dialogs/dialog-base/dialog-base';
import {InputItemSelection} from '../../../../../shared/components/input-item-selection/input-item-selection';
import {TranslateSafePipe} from '../../../../../shared/pipes/translation.pipe';
import {AlarmRuleActionDto, AlarmRuleActionType} from '../../../../../types/dtos/alarm-rule-action.dto';
import {InputSelectInterface} from '../../../../../types/interfaces/input.interface';
import {BaseRuleAction} from './rule-actions/base-rule-action';

@Component({
  selector: 'app-alarm-rule-actions',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonIconComponent,
    DialogBase,
    InputItemSelection,
    Skeleton,
    TranslateSafePipe,
  ],
  templateUrl: './alarm-rule-actions.html',
  styleUrl: './alarm-rule-actions.css',
})
export class AlarmRuleActions {
  @Input()
  mode: 'create' | 'override' | 'edit' | 'view' = 'view';

  @Input()
  public formGroup!: FormGroup;

  @Input()
  public isLoading: boolean = false;

  @Input()
  public actions: AlarmRuleActionDto[] = [];
  public addDialogOpen: boolean = false;
  public createGroup: FormGroup = new FormGroup({
    type: new FormControl(),
  });
  public TypeSelect: InputSelectInterface = {
    formGroup: this.createGroup,
    isTranslated: true,
    label: 'common.input.select-alarm-rule-condition.label',
    required: true,
    id: 'type',
    options: [],
  };

  private _actionDefinitions!: Record<AlarmRuleActionType, BaseRuleAction>;

  @Input({required: true})
  public set actionDefinitions(value: Record<AlarmRuleActionType, BaseRuleAction>) {
    this._actionDefinitions = value;
    this.TypeSelect.options = Object.values(AlarmRuleActionType)
      .map((type) => {
        let definition: BaseRuleAction = this._actionDefinitions[type];

        return {
          label: definition?.getName() ?? 'Unknown',
          value: type,
          description: definition?.getDescription() ?? '',
          icon: definition?.getIcon() ?? 'question_mark',
        };
      });
  }

  public get actionsForm(): FormArray<FormGroup> {
    return this.formGroup.get('actions') as FormArray<FormGroup>;
  }

  public createActionOnClick(): void {
    this.addDialogOpen = true;
  }

  public onTypeSelect(type: string): void {
    this.addDialogOpen = false;

    this.addCondition(type as AlarmRuleActionType);
  }

  public addCondition(type: AlarmRuleActionType): void {
    this.actionsForm.push(this._actionDefinitions[type].createFormGroup({
      type,
      config: {},
    }));
  }

  public removeAction(index: number): void {
    this.actionsForm.removeAt(index);
  }

  public getActionType(index: number): AlarmRuleActionType | null {
    return this.actionsForm.at(index)
      .get('type')?.value;
  }

  public getDefinition(type: AlarmRuleActionType): any {
    return this._actionDefinitions[type];
  }

  public getName(index: number): string {
    let type: AlarmRuleActionType | null = this.getActionType(index);
    if (!type) return '';
    return this._actionDefinitions[type]?.getName() ?? 'Unknown';
  }

  public getIcon(index: number): string {
    let type: AlarmRuleActionType | null = this.getActionType(index);
    if (!type) return '';
    return this._actionDefinitions[type]?.getIcon() ?? 'question_mark';
  }

  public getAction(index: number): AlarmRuleActionDto | undefined {
    let id = this.actionsForm.at(index)
      ?.get('id')?.value;
    return this.actions.find((a) => a.id === id);
  }
}
